/**
 * @copyright © 2026 ASAP Technology. All rights reserved.
 * @license BSL-1.1 (see LICENSE file)
 *
 * @file PaymentForm.tsx
 * @description Componente principal del formulario de pago: captura y validación de tarjeta, 3DS, QR, estilos dinámicos y modelo embebido (postMessage).
 */
"use client";

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  CreditCardIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import {
  detectCardBrand,
  validateLuhn,
  validateExpiry,
  validateCVV,
  formatCardNumber,
  getMaxCardLength,
  type CardBrand,
} from "@/utils/cardValidation";

import { captureDeviceFingerprint, type DeviceFingerprint } from "@/utils/deviceFingerprint";
import { getDeviceSummary } from "@/utils/deviceSummary";
import { isEmbedded, resolveTargetOrigin, postToParent, EMBED_MSG, EMBED_API_VERSION } from "@/lib/embed";
import { buildMerchantRedirectUrl } from "@/lib/redirect";
import InteractiveCard from "./InteractiveCard";
import { BrandIcon } from "./BrandLogos";
import { QRDisplay } from "./QRDisplay";
import { StatusBanner } from "./StatusBanner";
import RequiredInformation, { type RequiredInformationGroup } from "./RequiredInformation";
import { sanitizeImageUrl } from "@/utils/security";
import {
  enforceFinalizationInvariant,
  isPaymentFlowFinalizable,
  isRecoverableTerminalStatus,
  StateChannel,
  type PaymentStatusPayload,
} from "@/lib/stateChannel";
import type { AvailablePaymentMethod } from "@/types/protocol";

import {
  formatAmountDisplay,
  parseLocalizedAmount,
  getDecimalSeparator,
  getGroupSeparator,
  getDecimalsForCurrencyCode,
} from "@/utils/currencyFormat";
import { useLocale } from "@/utils/LocaleContext";

export interface StylesText {
  font?: string;
  color?: string;
  size?: string;
}

export interface StylesBackground {
  color?: string;
}

export interface StylesForm {
  form_label?: string;
  background_color?: string;
  button_color?: string;
  button_text_color?: string;
  button_hover_color?: string;
  button_disabled_color?: string;
  icon_color?: string;
  input_color?: string;
  input_innertext_color?: string;
  input_focus_color?: string;
  input_border_color?: string;
  input_border_radius?: string;
  label_font_size?: string;
  border_radius?: string;
  price_color?: string;
  error_color?: string;
  max_width?: string;
  padding?: string;
  show_cft?: boolean | string;
  show_tea?: boolean | string;
  show_interest_rate?: boolean | string;
  show_installment_calculation?: boolean | string;
  installment_calculation_mode?: string;
  header_align?: string;
  header_font?: string;
  header_size?: string;
  header_color?: string;
  button_align?: string;
  button_width?: string;
  button_padding?: string;
  button_font_size?: string;
  button_icon_url?: string;
  amount_font?: string;
  amount_font_size?: string;
  amount_color?: string;
}

export type StylesEntry =
  | { Text: StylesText }
  | { Background: StylesBackground }
  | { Form: StylesForm };

export interface Product {
  SKU: string;
  ImageUrl?: string;
  Category?: string;
  Name: string;
  UnitPrice: number;
  Quantity: number;
}

export interface StylesPayload {
  amount?: number;
  currency_code?: string;
  plan_id?: string;
  reference_number?: string;
  initial_token?: string;
  merchant_redirect_url?: string;
  redirect_delay_ms?: number;
  merchant_logo_url?: string;
  merchant_name?: string;
  product_layout?: string;
  fingerprint_level?: number;
  form_mode?: string;
  products?: Product[];
  enable_qr?: boolean;
  qr_base64?: string;
  transaction_valid_thru?: string;
  available_payment_methods?: AvailablePaymentMethod[];
  preferred_payment_method_id?: string;
  status_tracking_enabled?: boolean;
  configuration?: Record<string, unknown>;
  Styles: StylesEntry[];
}

interface FormData {
  cardNumber: string;
  cardHolder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

interface FormErrors {
  cardNumber?: string;
  cardHolder?: string;
  expiry?: string;
  cvv?: string;
}

interface PaymentPlan {
  planId: string;
  description: string;
  facilityPayments: number;
  interestRate: number;
  cft: number;
  tea: number;
}

interface PaymentMethodResponse {
  brand: string;
  brandDescription: string;
  cardType: string;
  plans: PaymentPlan[];
}

interface PaymentFormProps {
  readonly styles?: StylesPayload;
  readonly amount?: number;
  readonly currencyCode?: string;
  readonly planId?: string;
  readonly referenceNumber?: string;
  readonly initialToken?: string;
  readonly merchantRedirectUrl?: string;
  readonly redirectDelay?: number;
  readonly merchantLogoUrl?: string;
  readonly merchantName?: string;
  readonly productLayout?: string;
  readonly fingerprintLevel?: number;
  readonly formMode?: string;
  readonly products?: Product[];
  readonly enableQr?: boolean;
  readonly qrBase64?: string;
  readonly transactionValidThru?: string;
  readonly paymentMethods?: AvailablePaymentMethod[];
  readonly preferredPaymentMethodId?: string;
  readonly statusTrackingEnabled?: boolean;
  readonly configuration?: Record<string, unknown>;
  readonly formToken: string;
  readonly formConfig: {
    fields: Record<string, { name: string; id: string }>;
    honeypots: Array<{ name: string; id: string; type: string; position: number }>;
  };
  readonly requiredInformation?: RequiredInformationGroup[];
  readonly allowedEmbedderOrigins?: string[];
  readonly integrationMode?: string;   // redirect | embedded (Modelo A) | fields (Modelo B)
  readonly allowAmountEdit?: boolean;
  readonly showInstallments?: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  "032": "$",
  "840": "US$",
  "978": "€",
  "986": "R$",
  "152": "$",
  "170": "$",
  "484": "$",
  "604": "S/",
  "858": "$U",
  "826": "£",
  "600": "₲",
};

const CARD_BRAND_LABELS: Record<CardBrand, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  unknown: "",
};

function parseStyles(payload?: StylesPayload) {
  const text: StylesText = {};
  const background: StylesBackground = {};
  const form: StylesForm = {};

  if (!payload?.Styles) return { text, background, form };

  for (const entry of payload.Styles) {
    if ("Text" in entry) Object.assign(text, entry.Text);
    if ("Background" in entry) Object.assign(background, entry.Background);
    if ("Form" in entry) Object.assign(form, entry.Form);
  }

  return { text, background, form };
}

export default function PaymentForm({
  styles,
  amount,
  currencyCode,
  planId,
  referenceNumber,
  initialToken,
  merchantRedirectUrl,
  redirectDelay,
  merchantLogoUrl,
  merchantName,
  productLayout,
  fingerprintLevel,
  formMode,
  products,
  enableQr,
  qrBase64,
  transactionValidThru,
  paymentMethods,
  preferredPaymentMethodId,
  statusTrackingEnabled = false,
  configuration,
  formToken,
  formConfig,
  requiredInformation,
  allowedEmbedderOrigins,
  integrationMode,
  allowAmountEdit = false,
  showInstallments = true,
}: PaymentFormProps) {
  const [formData, setFormData] = useState<FormData>({
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [challengeUrl, setChallengeUrl] = useState<string | null>(null);
  const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethodResponse | null>(null);
  const [installments, setInstallments] = useState<number>(1);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [customAmount, setCustomAmount] = useState<string>("");
  const [fingerprint, setFingerprint] = useState<DeviceFingerprint | null>(null);
  const [isCvvFocused, setIsCvvFocused] = useState<boolean>(false);
  const [requiredInfoData, setRequiredInfoData] = useState<Record<string, string>>({});
  const [requiredInfoErrors, setRequiredInfoErrors] = useState<Record<string, string>>({});
  const resolvedPaymentMethods = useMemo<AvailablePaymentMethod[]>(() => {
    if (paymentMethods?.length) return paymentMethods;
    return enableQr
      ? [{ id: 'TRANSFER', kind: 'transfer', label: 'Transferencia' }]
      : [{ id: 'CARD', kind: 'card', label: 'Tarjeta' }];
  }, [enableQr, paymentMethods]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
    preferredPaymentMethodId ?? resolvedPaymentMethods[0]?.id ?? '',
  );
  const [flowState, setFlowState] = useState<PaymentStatusPayload>({
    status: 'pending',
    paymentCompleted: false,
    flowCompleted: false,
    canFinalizeOrder: false,
    responseActions: ['OK'],
    responseMessage: 'Esperando pago',
    retryTime: 3000,
    updatedAt: new Date().toISOString(),
  });
  const [activePayment, setActivePayment] = useState<{
    transactionAmount?: number;
    currencyCode: string;
    reference: string;
    qrBase64?: string;
    transactionValidThru?: string;
  }>({
    transactionAmount: amount,
    currencyCode: currencyCode ?? '032',
    reference: referenceNumber ?? '',
    qrBase64,
    transactionValidThru,
  });
  const [activeFormToken, setActiveFormToken] = useState(formToken);
  const redirectScheduled = useRef(false);
  const lastPaymentNotification = useRef('');

  useEffect(() => {
    if (!resolvedPaymentMethods.some((method) => method.id === selectedPaymentMethodId)) {
      setSelectedPaymentMethodId(
        preferredPaymentMethodId ?? resolvedPaymentMethods[0]?.id ?? '',
      );
    }
  }, [preferredPaymentMethodId, resolvedPaymentMethods, selectedPaymentMethodId]);

  const selectedPaymentMethod =
    resolvedPaymentMethods.find((method) => method.id === selectedPaymentMethodId)
    ?? resolvedPaymentMethods[0];
  const usesCardForm = selectedPaymentMethod?.kind === 'card';
  const paymentAttemptTerminated = isRecoverableTerminalStatus(flowState.status);
  const shouldTrackStatus = statusTrackingEnabled
    && !paymentAttemptTerminated
    && (!usesCardForm || flowState.paymentCompleted || flowState.status === 'processing');

  const { locale, t } = useLocale();

  // Capture device fingerprint once on mount
  // --- Modelo embebido (Modelo A) ---
  const embedded = useMemo(() => isEmbedded(), []);
  const embedTargetOrigin = useMemo(() => resolveTargetOrigin(allowedEmbedderOrigins ?? []), [allowedEmbedderOrigins]);

  const notifyPaymentCompleted = useCallback((payload: PaymentStatusPayload) => {
    const safePayload = enforceFinalizationInvariant(payload);
    if (!safePayload.paymentCompleted) {
      lastPaymentNotification.current = '';
      return;
    }
    const notificationKey = [
      safePayload.status,
      safePayload.remainingPayments ?? 'unknown',
      safePayload.flowCompleted,
    ].join(':');
    if (lastPaymentNotification.current === notificationKey) return;
    lastPaymentNotification.current = notificationKey;

    if (embedded) {
      postToParent(EMBED_MSG.PAYMENT_COMPLETED, {
        status: safePayload.status,
        flowCompleted: safePayload.flowCompleted,
        canFinalizeOrder: safePayload.canFinalizeOrder,
        reference: activePayment.reference,
      }, embedTargetOrigin);
    }
  }, [activePayment.reference, embedded, embedTargetOrigin]);

  const finishFlow = useCallback((payload: PaymentStatusPayload) => {
    const safePayload = enforceFinalizationInvariant(payload);
    if (!isPaymentFlowFinalizable(safePayload)) return;
    if (redirectScheduled.current) return;
    redirectScheduled.current = true;
    setFlowState(safePayload);

    setIsSuccess(true);

    if (embedded) {
      const eventPayload = {
        result: safePayload.status,
        canFinalizeOrder: true,
        reference: referenceNumber,
        amount,
        currencyCode,
      };
      postToParent(EMBED_MSG.FLOW_COMPLETED, eventPayload, embedTargetOrigin);
      // Evento 1.0 conservado para integraciones existentes.
      postToParent(EMBED_MSG.SUBMITTED, eventPayload, embedTargetOrigin);
      return;
    }

    if (merchantRedirectUrl) {
      const redirectTarget = buildMerchantRedirectUrl(
        merchantRedirectUrl,
        initialToken,
      );
      if (!redirectTarget) {
        console.error('Merchant redirect URL rejected because its scheme is not allowed.');
        return;
      }
      setTimeout(() => {
        // Sólo retorna el InitialToken público para que el comercio recupere
        // su InitialIdentification privado y ejecute OrderFinal S2S. El estado
        // nunca se confía al query string.
        window.location.href = redirectTarget.toString();
      }, redirectDelay ?? 3000);
    }
  }, [
    amount,
    currencyCode,
    embedded,
    embedTargetOrigin,
    initialToken,
    merchantRedirectUrl,
    redirectDelay,
    referenceNumber,
  ]);

  useEffect(() => {
    if (!shouldTrackStatus || !activeFormToken) return;

    const channel = new StateChannel({
      formToken: activeFormToken,
      configuration,
      onStateChange: (payload) => {
        notifyPaymentCompleted(payload);
        if (isRecoverableTerminalStatus(payload.status)) {
          setFlowState(enforceFinalizationInvariant(payload));
        } else if (payload.nextPayment) {
          setActivePayment(payload.nextPayment);
          setActiveFormToken(payload.nextPayment.formToken);
          const nextMethod = resolvedPaymentMethods.find(
            (method) => method.kind === payload.nextPayment?.methodKind,
          );
          if (nextMethod) setSelectedPaymentMethodId(nextMethod.id);
          // El resultado aprobado pertenece al tramo anterior. La vista pasa
          // inmediatamente al siguiente intento y el canal continúa.
          setFlowState({
            ...payload,
            status: 'pending',
            paymentCompleted: false,
            responseMessage: 'Esperando el siguiente pago',
          });
        } else {
          setFlowState(payload);
        }
        if (isPaymentFlowFinalizable(payload)) finishFlow(payload);
      },
    });
    channel.start();
    return () => channel.destroy();
  }, [
    activeFormToken,
    configuration,
    finishFlow,
    notifyPaymentCompleted,
    resolvedPaymentMethods,
    shouldTrackStatus,
  ]);

  // Cuando está embebido: anuncia 'ready' al parent y reporta el alto (responsive).
  useEffect(() => {
    if (!embedded) return;
    postToParent(EMBED_MSG.READY, { apiVersion: EMBED_API_VERSION, mode: integrationMode ?? 'embedded' }, embedTargetOrigin);
    const report = () => postToParent(EMBED_MSG.RESIZE, { height: Math.ceil(document.documentElement.scrollHeight) }, embedTargetOrigin);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [embedded, embedTargetOrigin, integrationMode]);

  useEffect(() => {
    const level = fingerprintLevel ?? 1;
    captureDeviceFingerprint(level).then(setFingerprint);
  }, [fingerprintLevel]);

  // Listen for 3DS challenge completion
  useEffect(() => {
    // Solo aceptar el resultado 3DS del origen del ACS que cargamos en el iframe.
    const expectedOrigin = challengeUrl ? new URL(challengeUrl, window.location.origin).origin : null;
    const handleMessage = (event: MessageEvent) => {
      if (expectedOrigin && event.origin !== expectedOrigin) return;
      if (event.data?.type === '3DS_RESULT') {
        setChallengeUrl(null);
        if (event.data.status === 'success') {
          const nextState: PaymentStatusPayload = {
            status: 'processing',
            paymentCompleted: false,
            flowCompleted: false,
            canFinalizeOrder: false,
            responseActions: ['OK'],
            responseMessage: 'Autenticación completada. Confirmando el pago…',
            retryTime: 2000,
            updatedAt: new Date().toISOString(),
          };
          setFlowState(nextState);
          if (!statusTrackingEnabled) {
            finishFlow({
              ...nextState,
              status: 'approved',
              paymentCompleted: true,
              flowCompleted: true,
              canFinalizeOrder: true,
              responseActions: ['OK', 'Approve'],
              responseMessage: 'Pago aprobado',
            });
          }
        } else {
          const refusedState: PaymentStatusPayload = {
            status: 'refused',
            paymentCompleted: true,
            flowCompleted: true,
            canFinalizeOrder: true,
            responseActions: ['Refuse'],
            responseMessage: 'Falló la autenticación 3D Secure.',
            retryTime: 3000,
            updatedAt: new Date().toISOString(),
          };
          setFlowState(refusedState);
          notifyPaymentCompleted(refusedState);
          finishFlow(refusedState);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    challengeUrl,
    finishFlow,
    notifyPaymentCompleted,
    statusTrackingEnabled,
  ]);

  useEffect(() => {
    if (activePayment.transactionAmount !== undefined) {
      const formatted = formatAmountDisplay(
        activePayment.transactionAmount,
        activePayment.currencyCode,
        locale,
      );
      setCustomAmount(formatted);
    }
  }, [activePayment.currencyCode, activePayment.transactionAmount, locale]);

  const { text, background, form } = useMemo(() => parseStyles(styles), [styles]);

  const themeStyles = useMemo(() => {
    const entries: [string, string | undefined][] = [
      ["--pf-font-family", text.font],
      ["--pf-text", text.color],
      ["--pf-font-size", text.size],
      ["--pf-background", background.color],
      ["--pf-card-bg", form.background_color],
      ["--pf-primary", form.button_color],
      ["--pf-btn-text", form.button_text_color],
      ["--pf-primary-hover", form.button_hover_color],
      ["--pf-primary-disabled", form.button_disabled_color],
      ["--pf-icon", form.icon_color],
      ["--pf-input-bg", form.input_color],
      ["--pf-input-text", form.input_innertext_color],
      ["--pf-input-focus", form.input_focus_color],
      ["--pf-input-border", form.input_border_color],
      ["--pf-input-border-radius", form.input_border_radius],
      ["--pf-label-font-size", form.label_font_size],
      ["--pf-border-radius", form.border_radius],
      ["--pf-price", form.price_color],
      ["--pf-error", form.error_color],
      ["--pf-max-width", form.max_width],
      ["--pf-padding", form.padding],
      ["--pf-header-align", form.header_align],
      ["--pf-header-font", form.header_font],
      ["--pf-header-size", form.header_size],
      ["--pf-header-color", form.header_color],
      ["--pf-btn-align", form.button_align],
      ["--pf-btn-width", form.button_width],
      ["--pf-btn-padding", form.button_padding],
      ["--pf-btn-font-size", form.button_font_size],
      ["--pf-amount-font", form.amount_font],
      ["--pf-amount-font-size", form.amount_font_size],
      ["--pf-amount-color", form.amount_color],
    ];
    const vars: Record<string, string> = {};
    for (const [cssVar, value] of entries) {
      if (value) vars[cssVar] = value;
    }
    return Object.keys(vars).length > 0 ? (vars as React.CSSProperties) : undefined;
  }, [text, background, form]);

  const numericAmount = parseLocalizedAmount(customAmount, locale);
  const currencySymbol = CURRENCY_SYMBOLS[activePayment.currencyCode] ?? "$";
  const decimalSep = getDecimalSeparator(locale);
  const groupSep = getGroupSeparator(locale);
  const currencyDecimals = getDecimalsForCurrencyCode(activePayment.currencyCode);

  const planBrand = useMemo(() => {
    if (paymentMethodData) return paymentMethodData.brand.toLowerCase();
    if (!planId) return null;
    if (planId.toUpperCase().startsWith("VISA")) return "visa";
    if (planId.toUpperCase().startsWith("MASTERCARD")) return "mastercard";
    return null;
  }, [planId, paymentMethodData]);

  const cardBrand = detectCardBrand(formData.cardNumber);

  // Helper to fetch payment method info
  const fetchPaymentMethod = useCallback(async (cardOrBin: string, isFullPan: boolean) => {
    try {
      const payload: Record<string, string> = { formToken: activeFormToken };
      if (isFullPan) {
        payload.cardNumber = cardOrBin;
      } else {
        payload.bin = cardOrBin;
      }
      
      const res = await fetch("/api/payment-method", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json() as PaymentMethodResponse;
        setPaymentMethodData(data);
        
        // Auto-select plan logic
        if (data.plans && data.plans.length > 0) {
          // If we have an externally requested planId, try to find it
          if (planId && !selectedPlanId) {
            const requested = data.plans.find(p => p.planId === planId);
            if (requested) {
              setSelectedPlanId(requested.planId);
              setInstallments(requested.facilityPayments);
              return;
            }
          }
          
          // Otherwise, if we already had a selected plan by the user, keep it if it still exists
          if (selectedPlanId) {
            const stillExists = data.plans.find(p => p.planId === selectedPlanId);
            if (stillExists) return; // keep current
          }
          
          // Fallback to first plan (usually 1 installment)
          setSelectedPlanId(data.plans[0].planId);
          setInstallments(data.plans[0].facilityPayments);
        }
      }
    } catch (e) {
      console.error("Failed to fetch payment method data", e);
    }
  }, [activeFormToken, planId, selectedPlanId]);

  // Effect to trigger payment method fetch
  useEffect(() => {
    const cleanNumber = formData.cardNumber.replaceAll(/\s/g, "");
    if (cleanNumber.length >= 6) {
      const isComplete = validateLuhn(cleanNumber) && cleanNumber.length >= 13;
      
      const timer = setTimeout(() => {
        fetchPaymentMethod(isComplete ? cleanNumber : cleanNumber.slice(0, 6), isComplete);
      }, isComplete ? 0 : 500); // Debounce BIN typing, immediate for complete PAN
      
      return () => clearTimeout(timer);
    } else {
      setPaymentMethodData(null);
    }
  }, [formData.cardNumber, fetchPaymentMethod]);

  const handleCardNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replaceAll(/\D/g, "");
    const brand = detectCardBrand(raw);
    const maxLen = getMaxCardLength(brand);
    const trimmed = raw.slice(0, maxLen);
    const formatted = formatCardNumber(trimmed);

    setFormData((prev) => ({ ...prev, cardNumber: formatted }));
    setErrors((prev) => ({ ...prev, cardNumber: undefined }));
  }, []);

  const handleCardHolderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "").toUpperCase();
    setFormData((prev) => ({ ...prev, cardHolder: value }));
    setErrors((prev) => ({ ...prev, cardHolder: undefined }));
  }, []);

  const handleExpiryMonthChange = useCallback((e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) val = val.slice(0, 2);
    setFormData((prev) => ({ ...prev, expiryMonth: val }));
    setErrors((prev) => ({ ...prev, expiry: undefined }));
  }, []);

  const handleExpiryYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) val = val.slice(0, 2);
    setFormData((prev) => ({ ...prev, expiryYear: val }));
    setErrors((prev) => ({ ...prev, expiry: undefined }));
  }, []);

  const handleCVVChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/\D/g, "");
    const maxLen = cardBrand === "amex" ? 4 : 3;
    setFormData((prev) => ({ ...prev, cvv: value.slice(0, maxLen) }));
    setErrors((prev) => ({ ...prev, cvv: undefined }));
  }, [cardBrand]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Validar número de tarjeta
    const cleanNumber = formData.cardNumber.replaceAll(/\s/g, "");
    if (!cleanNumber) {
      newErrors.cardNumber = t.errorCardRequired;
    } else if (!validateLuhn(cleanNumber)) {
      newErrors.cardNumber = t.errorCardInvalid;
    }

    // Validar titular
    if (!formData.cardHolder.trim()) {
      newErrors.cardHolder = t.errorHolderRequired;
    } else if (formData.cardHolder.trim().length < 3) {
      newErrors.cardHolder = t.errorHolderMinLength;
    }

    // Validar expiración
    if (!formData.expiryMonth || !formData.expiryYear) {
      newErrors.expiry = t.errorExpiryRequired;
    } else if (!validateExpiry(formData.expiryMonth, formData.expiryYear)) {
      newErrors.expiry = t.errorExpiryExpired;
    }

    // Validar CVV
    if (!formData.cvv) {
      newErrors.cvv = t.errorCvvRequired;
    } else if (!validateCVV(formData.cvv, cardBrand)) {
      newErrors.cvv = t.errorCvvDigits(cardBrand === "amex" ? 4 : 3);
    }

    setErrors(newErrors);

    // Validate RequiredInformation fields
    const riErrors: Record<string, string> = {};
    if (requiredInformation) {
      for (const group of requiredInformation) {
        for (const field of group.Fields) {
          if (field.Required && !requiredInfoData[field.FieldId]?.trim()) {
            riErrors[field.FieldId] = t.requiredFieldError;
          }
          if (field.Pattern && requiredInfoData[field.FieldId]) {
            const regex = new RegExp(field.Pattern);
            if (!regex.test(requiredInfoData[field.FieldId])) {
              riErrors[field.FieldId] = `${field.Label}: formato inválido`;
            }
          }
        }
      }
    }
    setRequiredInfoErrors(riErrors);

    return Object.keys(newErrors).length === 0 && Object.keys(riErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const cleanNumber = formData.cardNumber.replaceAll(/\s/g, "");
      const obfFields = formConfig?.fields;
      
      const payload: Record<string, unknown> = {
        formToken: activeFormToken,
        amount: numericAmount,
        currencyCode: activePayment.currencyCode,
        installments,
        referenceNumber: activePayment.reference,
        planId: selectedPlanId || undefined,
        deviceFingerprint: fingerprint,
        deviceInfo: getDeviceSummary(),   // anti-fraude: dispositivo real (vs ExpectedDevice del comercio)
        requiredInfo: Object.keys(requiredInfoData).length > 0 ? requiredInfoData : undefined,
      };
      
      if (obfFields) {
        payload[obfFields.cardNumber.name] = cleanNumber;
        payload[obfFields.cardHolder.name] = formData.cardHolder;
        payload[obfFields.expiryMonth.name] = formData.expiryMonth;
        payload[obfFields.expiryYear.name] = formData.expiryYear;
        payload[obfFields.cvv.name] = formData.cvv;
        
        // Add honeypot data if they typed anything in it
        formConfig?.honeypots.forEach(hp => {
          const input = document.getElementById(hp.id) as HTMLInputElement;
          if (input && input.value) {
            payload[hp.name] = input.value;
          }
        });
      } else {
        payload.cardNumber = cleanNumber;
        payload.cardHolder = formData.cardHolder;
        payload.expiryMonth = formData.expiryMonth;
        payload.expiryYear = formData.expiryYear;
        payload.cvv = formData.cvv;
      }

      const response = await fetch("/api/pay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === "success") {
        const submittedState = enforceFinalizationInvariant<PaymentStatusPayload>({
          status: 'approved',
          paymentCompleted: data.paymentCompleted ?? true,
          flowCompleted: data.flowCompleted ?? !statusTrackingEnabled,
          canFinalizeOrder: data.canFinalizeOrder ?? !statusTrackingEnabled,
          responseActions: data.responseActions ?? ['OK', 'Approve'],
          responseMessage: data.responseMessage ?? 'Pago aprobado',
          retryTime: data.retryTime ?? 2000,
          updatedAt: new Date().toISOString(),
        });
        setFlowState(submittedState);
        notifyPaymentCompleted(submittedState);
        if (isPaymentFlowFinalizable(submittedState)) {
          finishFlow(submittedState);
        }
      } else if (data.status === "challenge" && data.challengeUrl) {
        if (embedded) postToParent(EMBED_MSG.SUBMITTED, { result: 'challenge', challengeUrl: data.challengeUrl }, embedTargetOrigin);
        setChallengeUrl(data.challengeUrl);
      } else {
        const refusedState = enforceFinalizationInvariant<PaymentStatusPayload>({
          status: 'refused',
          paymentCompleted: data.paymentCompleted ?? true,
          flowCompleted: data.flowCompleted ?? false,
          canFinalizeOrder: data.canFinalizeOrder ?? false,
          responseActions: data.responseActions ?? ['Refuse'],
          responseMessage: data.responseMessage || 'Pago rechazado',
          retryTime: data.retryTime ?? 3000,
          updatedAt: new Date().toISOString(),
        });
        setFlowState(refusedState);
        notifyPaymentCompleted(refusedState);
        if (isPaymentFlowFinalizable(refusedState)) {
          finishFlow(refusedState);
        } else {
          setErrors({ cardNumber: data.responseMessage || t.errorCardInvalid });
        }
      }
    } catch {
      setErrors({ cardNumber: t.errorCardInvalid });
    } finally {
      setIsSubmitting(false);
      // PCI DSS: Clear sensitive data from memory immediately after processing
      setFormData({ cardNumber: "", cardHolder: "", expiryMonth: "", expiryYear: "", cvv: "" });
    }
  };

  if (challengeUrl) {
    return (
      <div className="payment-wrapper" style={themeStyles} role="main" aria-label={t.aria3dsChallenge}>
        <div className="payment-card relative z-10 mx-auto mt-4 overflow-hidden p-0" style={{ height: '550px', display: 'flex', backgroundColor: '#fff' }}>
          <iframe 
            src={challengeUrl} 
            title={t.aria3dsChallenge}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
    );
  }

  if (isSuccess) {
    const refused = flowState.status === 'refused';
    return (
      <div
        className="payment-success-wrapper"
        style={themeStyles}
        role="status"
        aria-live="polite"
        aria-label={refused ? t.ariaPayRefused : t.ariaPaySuccess}
      >
        <div className="payment-success-card">
          {refused ? (
            <XCircleIcon className="payment-declined-icon" aria-hidden="true" />
          ) : (
            <CheckCircleIcon className="payment-success-icon" aria-hidden="true" />
          )}
          <h2 className="payment-success-title">
            {refused ? t.refusedTitle : t.successTitle}
          </h2>
          <p className="payment-success-text">
            {refused
              ? flowState.responseMessage || t.refusedMessage
              : t.successMessage}
          </p>
          {!refused && !merchantRedirectUrl && (
            <button
              onClick={() => {
                setIsSuccess(false);
                setFormData({ cardNumber: "", cardHolder: "", expiryMonth: "", expiryYear: "", cvv: "" });
              }}
              className="payment-success-btn"
            >
              {t.successButton}
            </button>
          )}
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear() % 100;
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

  return (
    <div className="payment-wrapper" style={themeStyles} role="main" aria-label={t.ariaPaymentForm}>
      <div className="payment-card relative z-10 mx-auto mt-4">
        {/* Header */}
        <header className="payment-header">
          <div className="payment-header-left">
            {sanitizeImageUrl(merchantLogoUrl) ? (
              <img src={sanitizeImageUrl(merchantLogoUrl)} alt={merchantName || "Merchant Logo"} className="h-6 w-auto mr-2" />
            ) : (
              <svg className="payment-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
              </svg>
            )}
            <h1 className="payment-header-title">{merchantName || t.headerLabels[form.form_label ?? ""] || t.headerLabels["pedido"]} #{activePayment.reference}</h1>
          </div>
          {planBrand && (
            <div className="payment-header-brand">
              {planBrand === "visa" && (
                <img src="/icons/visa.svg" alt="Visa" className="payment-brand-icon" />
              )}
              {planBrand === "mastercard" && (
                <img src="/icons/mastercard.svg" alt="Mastercard" className="payment-brand-icon" />
              )}
            </div>
          )}
        </header>

        {/* Amount input */}
        <div className="payment-amount" role="group" aria-label={t.ariaAmountSection}>
          <label className="payment-amount-label" htmlFor="customAmount">{t.totalAmount}</label>
          <div className="payment-amount-field">
            <span className="payment-amount-currency">{currencySymbol}</span>
            <input
              id="customAmount"
              type="text"
              inputMode="decimal"
              placeholder={`0${decimalSep}${ "0".repeat(currencyDecimals)}`}
              value={customAmount}
              readOnly={!allowAmountEdit}
              onChange={(e) => {
                if (!allowAmountEdit) return;
                let chars = "0-9";
                if (decimalSep) chars += decimalSep === "." ? "." : decimalSep;
                if (groupSep) chars += groupSep;
                const allowed = new RegExp("[^" + chars + "]", "g");
                const val = e.target.value.replace(allowed, "");
                setCustomAmount(val);
              }}
              onBlur={() => {
                if (numericAmount > 0) {
                  setCustomAmount(formatAmountDisplay(numericAmount, activePayment.currencyCode, locale));
                }
              }}
              className={`payment-input payment-amount-input ${!allowAmountEdit ? 'readonly' : ''}`}
            />
          </div>
        </div>

        {/* Products list */}
        {products && products.length > 0 && (
          <div className={`payment-products ${productLayout === 'grid' ? 'grid grid-cols-2 gap-4' : productLayout === 'compact' ? 'space-y-1' : ''}`} role="list" aria-label={t.ariaProductsSection}>
            {products.map((product, index) => {
              const safeImg = sanitizeImageUrl(product.ImageUrl);
              return (
              <div key={product.SKU || index} className={`payment-product-item ${productLayout === 'compact' ? 'py-1 text-sm' : ''}`} role="listitem">
                {safeImg && productLayout !== 'compact' && (
                  <img
                    src={safeImg}
                    alt={product.Name}
                    className="payment-product-img"
                  />
                )}
                <div className="payment-product-info">
                  {product.Category && <span className="payment-product-type">{product.Category}</span>}
                  <span className="payment-product-desc">{product.Name}</span>
                  <span className="payment-product-code">{product.SKU}</span>
                  <span className="payment-product-qty">{t.quantity}: {product.Quantity}</span>
                </div>
                {product.UnitPrice != null && (
                  <span className="payment-product-price">
                    {currencySymbol}{formatAmountDisplay(product.UnitPrice, activePayment.currencyCode, locale)}
                  </span>
                )}
              </div>
            )})}
          </div>
        )}

        {/* RequiredInformation: dynamic fields from Core */}
        {requiredInformation && requiredInformation.length > 0 && (
          <RequiredInformation
            groups={requiredInformation}
            values={requiredInfoData}
            onChange={setRequiredInfoData}
            errors={requiredInfoErrors}
          />
        )}

        {resolvedPaymentMethods.length > 1 && !paymentAttemptTerminated && (
          <div className="mb-5" role="radiogroup" aria-label="Medio de pago">
            <p className="payment-label mb-2">Elegí cómo pagar</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {resolvedPaymentMethods.map((method) => {
                const selected = method.id === selectedPaymentMethod?.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setSelectedPaymentMethodId(method.id)}
                    className="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors"
                    style={{
                      borderColor: selected ? 'var(--pf-primary)' : 'var(--pf-input-border)',
                      backgroundColor: selected ? 'color-mix(in srgb, var(--pf-primary) 8%, white)' : 'var(--pf-input-bg)',
                    }}
                  >
                    {method.kind === 'card' ? (
                      <CreditCardIcon className="h-6 w-6" style={{ color: 'var(--pf-icon)' }} aria-hidden="true" />
                    ) : (
                      <span className="text-xl" aria-hidden="true">↗</span>
                    )}
                    <span className="font-medium">{method.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <StatusBanner
          status={flowState.status}
          message={flowState.responseMessage}
          configuration={configuration}
        />

        {/* Regular inputs are inside the form, InteractiveCard also goes inside */}
        {paymentAttemptTerminated ? null : usesCardForm ? (
        <form onSubmit={handleSubmit} noValidate aria-label={t.ariaCardSection}>
          {formMode === "interactive-card" && (
            <div className="mb-6">
              <InteractiveCard
                cardNumber={formData.cardNumber}
                cardHolder={formData.cardHolder}
                expiryMonth={formData.expiryMonth}
                expiryYear={formData.expiryYear}
                cvv={formData.cvv}
                brand={
                  paymentMethodData?.brand?.toLowerCase() as CardBrand ||
                  detectCardBrand(formData.cardNumber.replaceAll(/\s/g, ""))
                }
                isFlipped={isCvvFocused}
                isInteractiveMode={true}
                formConfig={formConfig}
                onChange={{
                  cardNumber: handleCardNumberChange,
                  cardHolder: handleCardHolderChange,
                  expiryMonth: handleExpiryMonthChange,
                  expiryYear: handleExpiryYearChange,
                  cvv: handleCVVChange
                }}
                onFocus={{
                  cvv: () => setIsCvvFocused(true),
                  blur: () => setIsCvvFocused(false)
                }}
              />
            </div>
          )}

          {/* Honeypots rendering (mixed across the form) */}
          {formConfig && (
            <div className="payment-hp-layer" aria-hidden="true">
              {formConfig.honeypots.map((hp) => (
                <div 
                  key={hp.id} 
                  className="payment-hp-field"
                  style={{
                    position: "absolute",
                    top: "-9999px",
                    left: "-9999px",
                  }}
                >
                  <label htmlFor={hp.id}>{hp.name}</label>
                  <input type={hp.type} id={hp.id} name={hp.name} tabIndex={-1} autoComplete="off" />
                </div>
              ))}
            </div>
          )}

          {/* Fila 1: Número + Titular (Hidden in Interactive Mode) */}
          {formMode !== "interactive-card" && (
            <div className="payment-row-2col">
              <div>
                <label className="payment-label" htmlFor={formConfig?.fields.cardNumber.id ?? "cardNumber"}>{t.cardNumber}</label>
                <div className="relative">
                  <input
                    id={formConfig?.fields.cardNumber.id ?? "cardNumber"}
                    name={formConfig?.fields.cardNumber.name ?? "cardNumber"}
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    value={formData.cardNumber}
                    onChange={handleCardNumberChange}
                    className={`payment-input ${errors.cardNumber ? "payment-input--error" : ""}`}
                    aria-invalid={errors.cardNumber ? "true" : undefined}
                    aria-describedby={errors.cardNumber ? "error-cardNumber" : undefined}
                  />
                  {cardBrand !== "unknown" && (
                    <span className="payment-brand-badge">
                      <BrandIcon brand={cardBrand} className="payment-brand-icon" />
                    </span>
                  )}
                </div>
                {errors.cardNumber && (
                  <p id="error-cardNumber" className="payment-error" role="alert">
                    <span aria-hidden="true">⚠ </span>{errors.cardNumber}
                  </p>
                )}
              </div>
              <div>
                <label className="payment-label" htmlFor={formConfig?.fields.cardHolder.id ?? "cardHolder"}>{t.cardHolder}</label>
                <input
                  id={formConfig?.fields.cardHolder.id ?? "cardHolder"}
                  name={formConfig?.fields.cardHolder.name ?? "cardHolder"}
                  type="text"
                  autoComplete="cc-name"
                  placeholder={t.cardHolderPlaceholder}
                  value={formData.cardHolder}
                  onChange={handleCardHolderChange}
                  className={`payment-input ${errors.cardHolder ? "payment-input--error" : ""}`}
                  aria-invalid={errors.cardHolder ? "true" : undefined}
                  aria-describedby={errors.cardHolder ? "error-cardHolder" : undefined}
                />
                {errors.cardHolder && (
                  <p id="error-cardHolder" className="payment-error" role="alert">
                    <span aria-hidden="true">⚠ </span>{errors.cardHolder}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Fila 2: Vencimiento + CVV + Cuotas */}
          <div className="payment-row-expiry">
            {formMode !== "interactive-card" && (
              <>
                <div className="payment-expiry-col">
              <label className="payment-label" htmlFor={formConfig?.fields.expiryMonth.id ?? "expiryMonth"}>{t.expiry}</label>
              <div className="payment-expiry-selects">
                <select
                  id={formConfig?.fields.expiryMonth.id ?? "expiryMonth"}
                  name={formConfig?.fields.expiryMonth.name ?? "expiryMonth"}
                  value={formData.expiryMonth}
                  onChange={handleExpiryMonthChange}
                  autoComplete="cc-exp-month"
                  className={`payment-select ${errors.expiry ? "payment-select--error" : ""}`}
                >
                  <option value="">{t.expiryMonthPlaceholder}</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                      {String(i + 1).padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <select
                  id={formConfig?.fields.expiryYear.id ?? "expiryYear"}
                  name={formConfig?.fields.expiryYear.name ?? "expiryYear"}
                  value={formData.expiryYear}
                  onChange={handleExpiryYearChange}
                  autoComplete="cc-exp-year"
                  className={`payment-select ${errors.expiry ? "payment-select--error" : ""}`}
                >
                  <option value="">{t.expiryYearPlaceholder}</option>
                  {years.map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
              {errors.expiry && (
                <p id="error-expiry" className="payment-error" role="alert">
                  <span aria-hidden="true">⚠ </span>{errors.expiry}
                </p>
              )}
            </div>
            <div className="payment-cvv-col">
              <label className="payment-label" htmlFor={formConfig?.fields.cvv.id ?? "cvv"}>{t.cvv}</label>
              <input
                id={formConfig?.fields.cvv.id ?? "cvv"}
                name={formConfig?.fields.cvv.name ?? "cvv"}
                type="text"
                inputMode="numeric"
                autoComplete="cc-csc"
                placeholder="123"
                value={formData.cvv}
                onChange={handleCVVChange}
                onFocus={() => setIsCvvFocused(true)}
                onBlur={() => setIsCvvFocused(false)}
                maxLength={cardBrand === "amex" ? 4 : 3}
                className={`payment-input ${errors.cvv ? "payment-input--error" : ""}`}
                disabled={isSubmitting || isSuccess}
                aria-invalid={errors.cvv ? "true" : undefined}
                aria-describedby={errors.cvv ? "error-cvv" : undefined}
              />
              {errors.cvv && (
                <p id="error-cvv" className="payment-error" role="alert">
                  <span aria-hidden="true">⚠ </span>{errors.cvv}
                </p>
              )}
            </div>
              </>
            )}
            {showInstallments &&
             paymentMethodData?.plans && 
             paymentMethodData.plans.length > 0 && 
             !(paymentMethodData.plans.length === 1 && paymentMethodData.plans[0].facilityPayments === 1) && 
             numericAmount > 0 && (
              <div className="payment-installments-col">
                <label className="payment-label" htmlFor="planId">{t.installments}</label>
                <select
                  id="planId"
                  value={selectedPlanId}
                  onChange={(e) => {
                    const selected = paymentMethodData.plans.find(p => p.planId === e.target.value);
                    if (selected) {
                      setSelectedPlanId(selected.planId);
                      setInstallments(selected.facilityPayments);
                    }
                  }}
                  className="payment-select"
                >
                  {paymentMethodData.plans.map((plan) => {
                    // Extraer y normalizar banderas de visualización
                    const showCft = form.show_cft !== false && form.show_cft !== "false";
                    const showInterestRate = form.show_interest_rate !== false && form.show_interest_rate !== "false";
                    const showTea = form.show_tea === true || form.show_tea === "true";
                    const showInstallmentCalc = form.show_installment_calculation === true || form.show_installment_calculation === "true";
                    const calcMode = form.installment_calculation_mode ?? "simple";

                    let amountPerInstallment = numericAmount / plan.facilityPayments;

                    if (showInstallmentCalc && plan.interestRate > 0 && plan.facilityPayments > 1) {
                      const n = plan.facilityPayments;
                      if (calcMode === "french_tna") {
                        const i_m = (plan.interestRate / 12) / 100;
                        amountPerInstallment = (numericAmount * i_m * Math.pow(1 + i_m, n)) / (Math.pow(1 + i_m, n) - 1);
                      } else if (calcMode === "french_tea" && plan.tea > 0) {
                        const i_m = Math.pow(1 + plan.tea / 100, 1 / 12) - 1;
                        amountPerInstallment = (numericAmount * i_m * Math.pow(1 + i_m, n)) / (Math.pow(1 + i_m, n) - 1);
                      } else if (calcMode === "cft" && plan.cft > 0) {
                        const i_m = Math.pow(1 + plan.cft / 100, 1 / 12) - 1;
                        amountPerInstallment = (numericAmount * i_m * Math.pow(1 + i_m, n)) / (Math.pow(1 + i_m, n) - 1);
                      } else { // "simple" o default
                        amountPerInstallment = (numericAmount * (1 + (plan.interestRate / 100) * (n / 12))) / n;
                      }
                    }

                    const coefs: string[] = [];
                    if (plan.interestRate > 0) {
                      if (showInterestRate) {
                        coefs.push(`TNA: ${plan.interestRate}%`);
                      }
                      if (showTea) {
                        coefs.push(`TEA: ${plan.tea}%`);
                      }
                      if (showCft) {
                        coefs.push(`CFT: ${plan.cft}%`);
                      }
                    }
                    const coefsSuffix = coefs.length > 0 ? ` (${coefs.join(" / ")})` : "";

                    return (
                      <option key={plan.planId} value={plan.planId}>
                        {plan.facilityPayments} {plan.facilityPayments === 1 ? t.installmentSingular : t.installmentPlural} {t.of} {currencySymbol}{formatAmountDisplay(amountPerInstallment, activePayment.currencyCode, locale)}
                        {coefsSuffix}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          {/* Fila 3: Botón pagar + SSL */}
          <div className="payment-footer">
            <button
              type="submit"
              disabled={isSubmitting}
              className="payment-submit-btn"
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="payment-spinner" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t.processing}
                </>
              ) : (
                <>
                  {form.button_icon_url ? (
                    <img
                      src={form.button_icon_url}
                      alt="Lock"
                      className="payment-btn-custom-icon"
                      style={{ width: "16px", height: "16px", objectFit: "contain" }}
                    />
                  ) : (
                    <LockClosedIcon className="payment-lock-icon" />
                  )}
                  {t.pay}
                </>
              )}
            </button>
            {/* <span className="payment-ssl-text">
              <LockClosedIcon className="payment-lock-icon" />
              Pago seguro SSL
            </span> */}
          </div>
        </form>
        ) : (
          <QRDisplay
            base64Image={activePayment.qrBase64}
            validThru={activePayment.transactionValidThru}
            instructions="Realizá la transferencia y mantené esta pantalla abierta. La confirmación es automática."
            configuration={configuration}
          />
        )}
      </div>
    </div>
  );
}
